import { cn } from "@/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import type {
  SidebarConfig,
  SidebarNavItem,
  StageNavItem,
} from "./sidebar-store";
import { SidebarUser } from "./sidebar-user";
import { QuickSwitchRow } from "./quick-switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { OperationGlyph, glyphColor } from "@/components/ui/operation-glyph";

const SIMPLE_WIDTH = 160;
const COMPLEX_WIDTH = 220;

interface EntitySidebarProps {
  config: SidebarConfig;
}

/**
 * Entity sidebar - shown alongside the global rail when viewing a specific entity.
 * The configuration is set by the entity's route component via useSidebarConfig.
 */
export function EntitySidebar({ config }: EntitySidebarProps) {
  const width = config.variant === "complex" ? COMPLEX_WIDTH : SIMPLE_WIDTH;

  return (
    <div
      className="flex flex-col h-svh bg-background border-r border-olive-6 shrink-0"
      style={{ width }}
    >
      {/* Back link + quick switch */}
      {config.parent && (
        <QuickSwitchRow
          parentHref={config.parent.href}
          parentSearch={config.parent.search}
          parentLabel={config.parent.label}
        />
      )}

      {/* Header section */}
      {config.customHeader ? (
        config.customHeader
      ) : config.header ? (
        <div className="px-3 py-2 border-b border-olive-6">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">
                {config.header.title}
              </div>
              {config.header.subtitle && (
                <div className="text-xs text-muted-foreground mt-0.5">
                  {config.header.subtitle}
                </div>
              )}
            </div>
            {config.header.actions && (
              <div className="flex items-center gap-1 shrink-0">
                {config.header.actions}
              </div>
            )}
          </div>
          {(config.header.status || config.header.meta) && (
            <div className="flex items-center gap-2 mt-2">
              {config.header.status}
              {config.header.meta}
            </div>
          )}
        </div>
      ) : null}

      {/* Live readout - above the navigation so it survives every page change */}
      {config.readout}

      {/* Navigation */}
      {/* Radix wraps the viewport content in a display:table div that grows to
          fit its content, so long rows would push past the edge instead of truncating */}
      <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:block!">
        <nav className="py-1">
          {config.navigation?.map((item) => (
            <NavItemRenderer key={item.id} item={item} />
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t border-olive-6 px-2 py-3">
        {config.footer ?? <SidebarUser />}
      </div>
    </div>
  );
}

function NavItemRenderer({ item }: { item: SidebarNavItem }) {
  if (item.type === "link") {
    return <NavLink item={item} />;
  }
  if (item.type === "section") {
    return <NavSection item={item} />;
  }
  if (item.type === "stages") {
    return <NavStages items={item.items} />;
  }
  if (item.type === "divider") {
    return <NavDivider />;
  }
  if (item.type === "custom") {
    return <>{item.content}</>;
  }
  return null;
}

function NavDivider() {
  return (
    <div className="py-2 px-3">
      <div className="border-t border-olive-6" />
    </div>
  );
}

function NavLink({
  item,
}: {
  item: Extract<SidebarNavItem, { type: "link" }>;
}) {
  const location = useLocation();
  const isActive = location.pathname === item.href.split("?")[0];
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 text-sm transition-colors",
        isActive
          ? "bg-primary/10 text-primary font-medium"
          : "text-muted-foreground hover:text-foreground hover:bg-muted",
      )}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span className="flex-1 truncate">{item.label}</span>
      {item.count !== undefined && (
        <span className="text-muted-foreground">{item.count}</span>
      )}
      {item.badge && (
        <span className="px-1 py-0.5 bg-muted rounded text-xs">{item.badge}</span>
      )}
    </Link>
  );
}

function NavSection({
  item,
}: {
  item: Extract<SidebarNavItem, { type: "section" }>;
}) {
  const [isExpanded, setIsExpanded] = useState(item.defaultExpanded ?? true);
  const location = useLocation();
  const hasActiveChild = item.children.some(
    (child) =>
      child.type === "link" &&
      (location.pathname === child.href ||
        location.pathname.startsWith(child.href + "/")),
  );
  const Icon = item.icon;

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors",
          hasActiveChild
            ? "text-foreground font-medium"
            : "text-muted-foreground hover:text-foreground hover:bg-muted",
        )}
      >
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="flex-1 text-left">{item.label}</span>
        {isExpanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
      </button>
      {isExpanded && (
        <div className="ml-3 border-l border-olive-6">
          {item.children.map((child) => (
            <NavItemRenderer key={child.id} item={child} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A plan: the session's tasks in the order they will be done.
 *
 * Flat on purpose. The obvious design — indent by how deep a task's branch sits
 * — was measured against real sessions and put every row at depth zero, because
 * a task groups its step across every arm and so belongs to the trunk. What does
 * vary is how many streams a task covers, so that is what a row says, and the
 * places the plan divides are drawn between rows instead of implied by shape.
 */
function NavStages({ items }: { items: StageNavItem[] }) {
  const location = useLocation();

  return (
    <div>
      {items.map((item) =>
        item.kind === "fork" ? (
          <ForkRow key={item.key} item={item} />
        ) : (
          <StageRow
            key={item.href}
            item={item}
            isActive={location.pathname === item.href}
          />
        ),
      )}
    </div>
  );
}

function StageRow({
  item,
  isActive,
}: {
  item: Extract<StageNavItem, { kind: "stage" }>;
  isActive: boolean;
}) {
  return (
    <Link
      to={item.href}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 text-sm transition-colors",
        isActive
          ? "bg-primary/10 text-primary font-medium"
          : "text-muted-foreground hover:text-foreground hover:bg-muted",
      )}
    >
      {/* Family colour is carried even when the row is not active, so a step is
          the same mark here that it is in the planner. */}
      <span style={{ color: glyphColor(item.icon) }} className="shrink-0">
        <OperationGlyph icon={item.icon} size={14} />
      </span>
      <span className="flex-1 truncate">{item.label}</span>
      {item.streams !== null && (
        <span
          className="shrink-0 font-mono text-xs text-olive-9"
          title={`Runs on ${item.streams} stream${item.streams === 1 ? "" : "s"}`}
        >
          ×{item.streams}
        </span>
      )}
    </Link>
  );
}

/**
 * Where the plan divides. Inert: it is not a page, and making it clickable
 * would put two kinds of row in a list whose every other row is a destination.
 */
function ForkRow({ item }: { item: Extract<StageNavItem, { kind: "fork" }> }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-1 text-xs text-olive-9">
      <span className="h-px w-2 shrink-0 bg-olive-6" />
      <span className="font-mono text-olive-11">{item.arms.join(" · ")}</span>
      {item.multiplicity > 1 && (
        <span className="font-mono text-olive-9">×{item.multiplicity}</span>
      )}
      <span className="h-px flex-1 bg-olive-6" />
    </div>
  );
}
